import {
  createContext,
  useContext,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import { useComposerRuntime, type Attachment } from '@assistant-ui/react';
import {
  largeClipboardText,
  pastedText,
  PastedTextFile,
  restorePastedText,
} from '@/lib/pasted-text';
import { restoreAttachmentOrder } from '@/lib/composer-attachment-order';
import { useChatCopy } from '@/lib/chat-i18n';
import type { TextSelection } from '@/speech/interactive/insert';

type PasteActions = {
  restore: (attachment: Attachment) => Promise<void>;
  remove: (attachment: Attachment) => Promise<void>;
  save: (attachment: Attachment, text: string) => Promise<void>;
};
export const PastedTextComposerContext = createContext<PasteActions | null>(null);
export const usePastedTextComposer = () => useContext(PastedTextComposerContext);

type Snapshot = { text: string; attachments: readonly Attachment[] };
function sameDraft(a: Snapshot, b: Snapshot) {
  return (
    a.text === b.text &&
    a.attachments.length === b.attachments.length &&
    a.attachments.every((attachment, index) => attachment === b.attachments[index])
  );
}

export function useLargePaste(
  input: RefObject<HTMLTextAreaElement | null>,
  selection: RefObject<TextSelection | null>,
) {
  const composer = useComposerRuntime();
  const copy = useChatCopy();
  const [error, setError] = useState('');
  const [replaying, setReplaying] = useState(false);
  const undo = useRef<{ before: Snapshot; after: Snapshot; reversed: boolean } | null>(null);
  const busy = useRef(false);
  const snapshot = (): Snapshot => {
    const { text, attachments } = composer.getState();
    return { text, attachments };
  };
  const placeCaret = (text: string, at: number, frames = 0) =>
    requestAnimationFrame(() => {
      const element = input.current;
      if (!element) return;
      // The runtime store publishes after the event. Wait for the controlled
      // textarea's commit before restoring its selection.
      if (element.value !== text.replaceAll('\r\n', '\n').replaceAll('\r', '\n')) {
        if (frames < 10) placeCaret(text, at, frames + 1);
        return;
      }
      const caret = text.slice(0, at).replaceAll('\r\n', '\n').replaceAll('\r', '\n').length;
      element.focus({ preventScroll: true });
      element.setSelectionRange(caret, caret);
      selection.current = { start: caret, end: caret };
    });
  const remove = async (attachment: Attachment) => {
    const index = composer.getState().attachments.findIndex((item) => item.id === attachment.id);
    if (index >= 0) await composer.getAttachmentByIndex(index).remove();
  };
  const change = async (action: () => Promise<void>) => {
    if (busy.current) return;
    busy.current = true;
    const before = snapshot();
    setError('');
    try {
      await action();
      const after = snapshot();
      if (!sameDraft(before, after)) undo.current = { before, after, reversed: false };
    } catch {
      setError(copy.uploadFailed);
    } finally {
      busy.current = false;
    }
  };
  const actions: PasteActions = {
    remove: (attachment) => change(() => remove(attachment)),
    save: (attachment, text) =>
      change(() =>
        composer.addAttachment(new PastedTextFile(text, attachment.name, attachment.id)),
      ),
    restore: (attachment) =>
      change(async () => {
        const paste = pastedText(attachment);
        if (paste === null) return;
        const text = composer.getState().text;
        const at = selection.current ?? { start: text.length, end: text.length };
        const next = restorePastedText(text, at.start, at.end, paste);
        await remove(attachment);
        composer.setText(next.text);
        placeCaret(next.text, next.caret);
      }),
  };
  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const text = largeClipboardText(event.clipboardData);
    if (text === null) return;
    event.preventDefault();
    void change(() => composer.addAttachment(new PastedTextFile(text)));
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    const entry = undo.current;
    if (
      event.nativeEvent.isComposing ||
      !(event.ctrlKey || event.metaKey) ||
      event.altKey ||
      !entry ||
      busy.current
    )
      return;
    const redo =
      event.key.toLowerCase() === 'y' || (event.key.toLowerCase() === 'z' && event.shiftKey);
    if (event.key.toLowerCase() !== 'z' && !redo) return;
    if (redo !== entry.reversed) return;
    if (!sameDraft(snapshot(), entry.reversed ? entry.before : entry.after)) {
      undo.current = null; // Typing, sending or another operation owns the native undo now.
      return;
    }
    event.preventDefault();
    const target = entry.reversed ? entry.after : entry.before;
    busy.current = true;
    setReplaying(true);
    void (async () => {
      try {
        await restoreAttachmentOrder(composer, target.attachments);
        composer.setText(target.text);
        entry.reversed = !entry.reversed;
        if (entry.reversed) entry.before = snapshot();
        else entry.after = snapshot();
        placeCaret(target.text, target.text.length);
      } catch {
        setError(copy.uploadFailed);
      } finally {
        busy.current = false;
        setReplaying(false);
      }
    })();
  };
  return { actions, onPaste, onKeyDown, error, replaying };
}
